
import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";
import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";

import {
  DocumentAPIResponse,
  DocumentUploadProps,
  FetchDocumentResponseType,
  UploadedDocumentsListing,
} from "./types";

import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { downloadFileAccordingToExtension } from "src/helpers/downloadFile";
import { useNavigate } from "react-router-dom";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const ALLOWED_FILE_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".tiff",
  ".tif",
  ".ccd",
  ".cda",
  ".ccr",
  ".ccda",
];

const isAllowedFileExtension = (fileName: string) => {
  const extension = `.${fileName.split(".").pop()?.toLowerCase()}`;
  return ALLOWED_FILE_EXTENSIONS.includes(extension);
};

const DocumentUpload: FC<DocumentUploadProps> = ({
  caseId,
  caseDocuments,
  setNotes,
  title,
}): JSX.Element => {
  const [caseFile, changeCaseFile] = useState<FileList | null>(null);
  const [fileName, changeFileName] = useState<string>("");
  const [caseDocumentsList, changeCaseDocumentsListing] =
    useState<UploadedDocumentsListing[]>(caseDocuments);

  const [caseDocumentType, changeCaseDocumentType] = useState<string[]>([]);
  const [documentTypeErr, updateDocumentTypeErr] = useState<string>("");

  const ref = useRef<HTMLInputElement>(null);

  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);

  const navigate = useNavigate();

  const reset = () => {
    changeCaseFile(null);
    changeCaseDocumentType([]);

    if (ref.current) {
      ref.current.value = "";
    }
  };

  useEffect(() => {
    if (documentTypeErr.length === 0) return;

    if (caseDocumentType.length > 0) {
      updateDocumentTypeErr("");
    }
  }, [caseDocumentType, documentTypeErr]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files;

    if (selectedFile && selectedFile.length > 0) {
      const file = selectedFile[0];

      if (!isAllowedFileExtension(file.name)) {
        showToast(
          [
            `Invalid file format. Allowed formats are: ${ALLOWED_FILE_EXTENSIONS.join(
              ", "
            )}`,
          ],
          ToastTypes.ERROR
        );

        changeCaseFile(null);

        if (ref.current) {
          ref.current.value = "";
        }

        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        showToast(["File size should not exceed 25 MB"], ToastTypes.ERROR);

        changeCaseFile(null);

        if (ref.current) {
          ref.current.value = "";
        }

        return;
      }

      changeCaseFile(selectedFile);
    } else {
      changeCaseFile(null);
    }
  };

  const {
    error: uploadFileError,
    data: uploadFileResponse,
    mutate: uploadCaseDocument,
  } = useFetch<DocumentAPIResponse>(APIEndpoints.UPLOAD_CASE_FILE);

  useEffect(() => {
    if (uploadFileError) {
      hideLoader();
      changeCaseFile(null);
    }

    if (uploadFileResponse) {
      hideLoader();

      const { data } = uploadFileResponse;

      setNotes((prevNotes) => [data.note, ...prevNotes]);

      changeCaseDocumentsListing((prevList) => [
        ...prevList,
        {
          ...data,
          created_at: data.note.created_at,
          id: "",
        },
      ]);

      showToast(["Document uploaded successfully"], ToastTypes.SUCCESS);

      reset();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileResponse, uploadFileError]);

  const uploadForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (caseDocumentType.length === 0) {
      updateDocumentTypeErr("Select at least one option to proceed");
      return;
    }

    if (!caseFile || caseFile.length === 0) {
      showToast(["Please select a file to upload"], ToastTypes.ERROR);
      return;
    }

    const file = caseFile[0];

    if (!isAllowedFileExtension(file.name)) {
      showToast(
        [
          `Invalid file format. Allowed formats are: ${ALLOWED_FILE_EXTENSIONS.join(
            ", "
          )}`,
        ],
        ToastTypes.ERROR
      );

      changeCaseFile(null);

      if (ref.current) {
        ref.current.value = "";
      }

      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast(["File size should not exceed 25 MB"], ToastTypes.ERROR);

      changeCaseFile(null);

      if (ref.current) {
        ref.current.value = "";
      }

      return;
    }

    if (documentTypeErr.length > 0) {
      updateDocumentTypeErr("");
    }

    let finalDocumentType = "";

    if (caseDocumentType.includes("clinicals")) {
      finalDocumentType = "clinicals";
    } else if (caseDocumentType.includes("insurance_information")) {
      finalDocumentType = "insurance_information";
    } else {
      finalDocumentType = "other";
    }

    setLoader();

    const formData = new FormData();

    formData.append("file", file);
    formData.append("type", finalDocumentType);
    formData.append("all_types", caseDocumentType.join(","));

    uploadCaseDocument(formData, { caseId });
  };

  const {
    data: fetchDocumentData,
    error: fetchDocumentError,
    mutate: fetchDocument,
  } = useFetch<FetchDocumentResponseType>(
    APIEndpoints.FETCH_DOCUMENT,
    {},
    {},
    false
  );

  useEffect(() => {
    if (fetchDocumentData || fetchDocumentError) {
      hideLoader();

      if (fetchDocumentData) {
        const { data } = fetchDocumentData;
        downloadFileAccordingToExtension(data, fileName);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDocumentData, fetchDocumentError]);

  const showFetchedFile = (
    blobId: number,
    fileName: string,
    fetchType: string,
    idNumber: string | null
  ) => {
    setLoader();
    changeFileName(fileName);

    if (fetchType === "legacy") {
      fetchDocument({}, {}, { type: fetchType, id: idNumber });
    } else {
      fetchDocument(
        {},
        {},
        {
          blob_id: blobId,
          case_id: caseId,
          type: fetchType || "fetch",
        }
      );
    }
  };

  const updateClinicalValue = (
    e: ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const { checked } = e.target;

    changeCaseDocumentType((prev) => {
      if (checked) {
        return prev.includes(name) ? prev : [...prev, name];
      }

      return prev.filter((item) => item !== name);
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h1 className="m-0">{title || "Documents"}</h1>
      </div>

      <div className="card-body">
        <div className="mb-3">
          <div
            className={`container-fluid case-notes-listing ${
              caseDocumentsList && caseDocumentsList.length > 0 ? "mb-4" : ""
            }`}
          >
            {caseDocumentsList && caseDocumentsList.length > 0
              ? caseDocumentsList.map((doc, index) => (
                  <div
                    className={`row py-1 align-items-center ${
                      index % 2 === 0 ? "bg-secondary-subtle" : ""
                    }`}
                    key={`${doc.blob_id}-${index}`}
                  >
                    <div className="col-lg-7 col-8">
                      <button
                        type="button"
                        onClick={() =>
                          showFetchedFile(
                            doc.blob_id,
                            doc.filename,
                            doc.type,
                            doc.id
                          )
                        }
                        className="btn btn-link link-underline link-underline-opacity-0 link-offset-0-hover link-underline-opacity-75-hover p-0 align-baseline fs-6 text-start text-break"
                      >
                        {doc ? doc.filename : ""}
                      </button>
                    </div>

                    <div className="col-lg-2 col-4 text-start">
                      <p className="mb-0 fs-6">
                        {doc ? `${doc.size_in_kb} kb` : ""}
                      </p>
                    </div>

                    <div className="col-lg-3 col-12">
                      <p className="mb-0 fs-6">
                        {doc ? doc.created_at : ""}
                      </p>
                    </div>
                  </div>
                ))
              : null}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h6 className="text-black my-1">
              <i className="bi bi-cloud-arrow-up-fill fs-5 text-end"></i>{" "}
              Upload New Document
            </h6>
          </div>

          <div className="card-body">
            <form onSubmit={uploadForm}>
              <p className="fw-bold mb-1">
                Please select all that apply for the type of documents being
                uploaded
              </p>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="clinicals"
                  onChange={(e) => updateClinicalValue(e, "clinicals")}
                  checked={caseDocumentType.includes("clinicals")}
                  value="clinicals"
                />

                <label className="form-check-label" htmlFor="clinicals">
                  Clinicals/Medical Notes
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="insurance_information"
                  onChange={(e) =>
                    updateClinicalValue(e, "insurance_information")
                  }
                  checked={caseDocumentType.includes("insurance_information")}
                  value="insurance_information"
                />

                <label
                  className="form-check-label"
                  htmlFor="insurance_information"
                >
                  Insurance Information
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="other"
                  onChange={(e) => updateClinicalValue(e, "other")}
                  checked={caseDocumentType.includes("other")}
                  value="other"
                />

                <label className="form-check-label" htmlFor="other">
                  Other
                </label>
              </div>

              {documentTypeErr.length > 0 ? (
                <p className="mb-0 text-danger">
                  <small>{documentTypeErr}</small>
                </p>
              ) : null}

              <div className="input-group mt-3">
                <input
                  type="file"
                  className="form-control"
                  name="caseDocument"
                  onChange={handleFileChange}
                  ref={ref}
                  accept=".pdf,.doc,.docx,.tiff,.tif,.ccd,.cda,.ccr,.ccda"
                />

                <button
                  disabled={caseFile === null}
                  className="btn btn-primary rounded-end"
                  type="submit"
                  id="inputGroupFileAddon04"
                >
                  <div className="row align-items-center px-1">
                    <p className="col-auto mb-0">Upload</p>
                  </div>
                </button>
              </div>

              <p className="mt-2 mb-0 text-muted">
                <small>
                  Allowed formats:{" "}
                  {ALLOWED_FILE_EXTENSIONS.join(", ")}. Maximum file size: 25 MB.
                </small>
              </p>
            </form>
          </div>
        </div>

        {title && title.length > 0 ? (
          <div className="d-flex flex-fill justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-primary py-1"
              onClick={() => navigate("/cases")}
            >
              Skip
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DocumentUpload;
